import { CHOOSER_OTHER_INPUT_KEY, firstTruthy, firstDefined, choiceKey, resolveChooserAnswer, resolveChoiceAction } from "./chooserSelection.js";
export function fixedActionInputValues(selectedOptions, customInput) {
  return selectedOptions.map((item) => (
    [item.allowCustomInput, item.requiresCustomInput].some(Boolean)
      ? customInput
      : firstDefined(item.answerValue, item.text)
  ));
}

export function actionInputValueForPage({ page, index, chooserSelections, chooserCustomInputs }) {
  const pageRecord = Object.assign({}, page);
  const pageKey = String(index + 1);
  const customInput = String(firstDefined(chooserCustomInputs[pageKey], '')).trim();
  if (pageRecord.inputKind === 'free_text') {
    return { fieldKey: String(pageRecord.fieldKey || '').trim(), value: customInput };
  }
  return choiceInputValueForPage({ pageRecord, pageKey, chooserSelections, customInput });
}

export function choiceInputValueForPage({ pageRecord, pageKey, chooserSelections, customInput }) {
  const pageOptions = Array.isArray(pageRecord.options) ? pageRecord.options : [];
  const selected = Array.isArray(chooserSelections[pageKey]) ? chooserSelections[pageKey] : [];
  const selectedOptions = pageOptions.filter((item) => selected.includes(choiceKey(item)));
  const selectedOtherInput = [
    pageRecord.allowOtherInput === true,
    selected.includes(CHOOSER_OTHER_INPUT_KEY),
  ].every(Boolean);
  const fixedValues = fixedActionInputValues(selectedOptions, customInput);
  const multipleValues = [...fixedValues, ...(selectedOtherInput ? [customInput] : [])]
    .filter(Boolean);
  const singleValue = selectedOtherInput
    ? customInput
    : resolveChooserAnswer(selectedOptions[0], customInput);
  const value = pageRecord.selectionMode === 'multiple' ? multipleValues : singleValue;
  return { fieldKey: String(pageRecord.fieldKey || '').trim(), value };
}

export function actionInputValuesForPages({ chooserPages, chooserSelections, chooserCustomInputs }) {
  return chooserPages.map((page, index) => actionInputValueForPage({
    page, index, chooserSelections, chooserCustomInputs,
  })).filter((item) => item.fieldKey);
}

export function selectedOtherInputChoice(page, selectedKeys) {
  const selected = Array.isArray(selectedKeys) ? selectedKeys : [];
  return page?.allowOtherInput === true && selected.includes(CHOOSER_OTHER_INPUT_KEY)
    ? {
      key: CHOOSER_OTHER_INPUT_KEY,
      text: '其他',
      answerValue: '其他',
      allowCustomInput: true,
      source: 'action_input',
    }
    : null;
}

export function actionInputValueIsReady(item) {
  return Array.isArray(item?.value)
    ? item.value.length > 0
    : Boolean(String(item?.value || '').trim());
}

export function actionInputIsReady(chooser, actionInputValues) {
  return chooser.kind !== 'action_input' || actionInputValues.every(actionInputValueIsReady);
}

export function resolveActionInputPageProgress({
  chooserKind,
  actionInputValues,
  normalizedChooserPage,
  chooserPages,
}) {
  const isActionInput = chooserKind === 'action_input';
  const currentValue = actionInputValues[normalizedChooserPage - 1];
  return {
    allPagesReady: !isActionInput || actionInputValues.every(actionInputValueIsReady),
    currentPageReady: !isActionInput || actionInputValueIsReady(currentValue),
    isLastPage: normalizedChooserPage >= chooserPages.length,
  };
}

export function selectionKeysForFocusedChoice({ selectionMode, selectedKeys, focusedKey }) {
  const currentKeys = Array.isArray(selectedKeys) ? selectedKeys : [];
  const normalizedKey = String(focusedKey || '').trim();
  return selectionMode === 'multiple'
    ? currentKeys
    : [normalizedKey].filter(Boolean);
}

export function resolveChooserSelectionUpdate({ selectionMode, currentKeys, nextKeys }) {
  const current = Array.isArray(currentKeys) ? currentKeys.map(String) : [];
  const next = Array.isArray(nextKeys) ? nextKeys.map(String) : [];
  const repeatsSelectedMultiChoice = [
    selectionMode === 'multiple',
    next.length < current.length,
    next.every((key) => current.includes(key)),
  ].every(Boolean);
  return repeatsSelectedMultiChoice
    ? { keys: current, shouldAdvance: true }
    : { keys: next, shouldAdvance: false };
}

export function chooserSelectionNeedsInitialization(initializedChooserIdentity, chooserIdentity) {
  const initialized = String(initializedChooserIdentity || '').trim();
  const current = String(chooserIdentity || '').trim();
  return Boolean(current) && initialized !== current;
}

export function resolveChooserSubmissionProgression({ chooserKind, pageProgress, actionInputReady }) {
  if (chooserKind !== 'action_input') {
    return actionInputReady ? 'submit' : 'blocked';
  }
  if (!pageProgress.isLastPage) {
    return pageProgress.currentPageReady ? 'advance' : 'blocked';
  }
  return actionInputReady ? 'submit' : 'blocked';
}

export function consumeChooserPageProgression({ progression, setChooserPage, pageCount }) {
  if (progression === 'blocked') {
    return true;
  }
  if (progression === 'advance') {
    setChooserPage((current) => Math.min(pageCount, current + 1));
    return true;
  }
  return false;
}

export function submitChooserSelection({
  onComposerChooserSubmit,
  onComposerActionSelect,
  composerChooser,
  selectedChoice,
  selectedChoiceRecord,
  chooserSelectedKey,
  chooserRecommendedKey,
  chooserCustomInput,
  actionInputValues,
  answerValue,
}) {
  if (typeof onComposerChooserSubmit === 'function') {
    onComposerChooserSubmit({
      composerChooser,
      selectedChoice,
      chooserSelectedKey,
      chooserRecommendedKey,
      chooserCustomInput,
      actionInputValues,
      answerValue,
    });
    return;
  }
  if (typeof onComposerActionSelect !== 'function') {
    return;
  }
  onComposerActionSelect({
    ...selectedChoice,
    source: String(firstTruthy(selectedChoiceRecord.source, 'action')),
    text: String(firstTruthy(selectedChoiceRecord.text, answerValue)).trim(),
    answerValue,
    action: resolveChoiceAction(selectedChoiceRecord),
  });
}

export function chooserIdentityFor(chooser) {
  return `${String(chooser.kind || '').trim()}:${String(chooser.key || '').trim()}`;
}

export function resolveChooserInputState({ currentChooserPage, selectedKeys, chooser }) {
  const chooserAllowsOtherInput = Object.assign({}, currentChooserPage).allowOtherInput === true;
  return {
    chooserAllowsOtherInput,
    chooserOtherInputSelected: [
      chooserAllowsOtherInput,
      selectedKeys.includes(CHOOSER_OTHER_INPUT_KEY),
    ].every(Boolean),
    chooserConfirmOnSelect: chooser.confirmOnSelect === true,
    chooserHideSubmit: chooser.hideSubmit === true,
  };
}

export function resolveChooserPrimaryAction({ chooserKind, pageProgress, actionInputReady, chooserFooter }) {
  const hasNextActionInputPage = [
    chooserKind === 'action_input',
    !pageProgress.isLastPage,
  ].every(Boolean);
  return {
    pageActionReady: hasNextActionInputPage ? pageProgress.currentPageReady : actionInputReady,
    label: hasNextActionInputPage ? '下一步' : String(firstTruthy(chooserFooter.submitLabel, '提交')),
  };
}
